import { Injectable } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SubmissionService, ValidateClientResponse } from '@xilo-mono/form-contracts';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConfigService } from '@xilo-mono/common/config';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthTokenResponse, AuthLoginResponse } from '../models/auth.model';

const jwtHelper = new JwtHelperService();

const USER_TYPE_ERR = 'Unexpected user type in payload'

type UserRole = 'admin' | 'agent' | 'client' | 'systemAdmin';

type LocalStorageTypes =
  'token' |
  'workflowsAccountId' |
  'agentId' |
  'userId' |
  'user' |
  'listViewSelection' |
  'XILO_DASHBOARD_FILTERS' |
  'features' |
  'isContractSigned' |
  'slackUrl' |
  'microsoftUrl' |
  'googleUrl' |
  'userType' |
  'copiedSection' |
  'copiedQuestionGroup' |
  'copiedQuestion' |
  'copiedObject' |
  'formAnalytics' |
  'userRole' |
  'showEdit';

export interface DecodedToken {
  id: number;
  type: UserRole;
  companyId: number;
}
export interface LocalStorageParams {
  token?                    : string;
  workflowsAccountId?       : number;
  agentId?                  : number;
  userId?                   : number;
  user?                     : any;
  userEmail?                : string;
  listViewSelection?        : string;
  XILO_DASHBOARD_FILTERS?   : any;
  features?                 : any;
  isContractSigned?         : boolean;
  slackUrl?                 : string;
  microsoftUrl?             : string;
  googleUrl?                : string;
  userType?                 : string;
  copiedSection?            : any;
  copiedQuestionGroup?      : any;
  copiedQuestion?           : any;
  copiedObject?             : any;
  formAnalytics?            : string;
  showEdit?                 : boolean;
  userRole?                 : UserRole
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  apiUrl = `${this.configService.apiUrl}/auth`;

  constructor(
    private submissionService:SubmissionService,
    private http: HttpClient,
    private configService: ConfigService
  ) { }

  verifyClientPortalToken(submissionId:number, body:{email:string,phone:string,firstName:string,lastName:string}):Observable<ValidateClientResponse>{
    return this.submissionService.checkSubmissionFromProspect(submissionId,body).pipe(
      tap((resp: ValidateClientResponse) => {
        if(resp.success === true) {
          localStorage.setItem('token', resp.token);
        }
      })
    )
  }

  /**
   * Checks the auth token provided to see if it is valid or not
   * @param token
   */
  verifyToken(clientId: number): Observable<AuthTokenResponse> {
    return this.http.get<AuthTokenResponse>(`${this.apiUrl}/check-token/client/${clientId}`);
  }

  /**
   * Checks the auth token provided to see if it is valid or not and if so returns a new token with more data
   * @param token
   */
  verifyAndGenerateNewToken(): Observable<AuthLoginResponse> {
    return this.http.get<AuthLoginResponse>(`${this.apiUrl}/verify-and-generate-token`);
  }

  isTokenExpired(token?: string): boolean {
    if (!token) token = localStorage.getItem('token');
    if (!token) return true;

    return jwtHelper.isTokenExpired(token);
  }

  /**
   * Universal method to decode a token and returns the decoded object
   * @returns {DecodedToken} - If token exists returns token
   */
  decodedToken(): DecodedToken {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return jwtHelper.decodeToken(token);
  }

  getUserFromAuth():DecodedToken {
    const payload = this.decodedToken()
    if (!payload) return;
    let result:DecodedToken

    if (payload['user']) {
      result = {
        id        : payload['user'].id,
        companyId : payload['user'].companyId,
        type      : 'admin',
      }
    } else if (payload['agent']) {
      result = {
        id        : payload['agent'].id,
        companyId : payload['agent'].companyId,
        type      : 'agent',
      }
    } else if (payload['client']) {
      result = {
        id        : payload['client'].id,
        companyId : payload['client'].companyClientId,
        type      : 'client',
      }
    } else {
      console.error(`${USER_TYPE_ERR} in ${JSON.stringify(payload)}`)
    }

    return result
  }

  /**
   * Universal method to clear all potential params from local storage
   * @param {any} params - Additional keys we want to remove from localStorage
   */
  clearStorage(params?: LocalStorageTypes[]): boolean {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('agentId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('agentId');
    localStorage.removeItem('isCustomer');
    localStorage.removeItem('workflowsAccountId');
    localStorage.removeItem('userType');
    localStorage.removeItem('subId');
    localStorage.removeItem('googleUrl');
    localStorage.removeItem('features');
    localStorage.removeItem('XILO_DASHBOARD_FILTERS');
    localStorage.removeItem('userRole');
    if (params && params.length > 0) {
      params.forEach(param => {
        localStorage.removeItem(param);
      })
    }
    return true;
  }

  /**
   * Sets the values of local storage based on the params passed
   * This is only for the dashboard app for now
   * @param {LocalStorageParams}    params - Object referencing the key / value pairs to storage in local storage
   * --
   * @param {string} token - The authenticated token for this user
   * @param {number} workflowsAccountId? - The workflow accounts user id returned from the workflows api
   * @param {number} agentId? - The id of the logged in agent
   * @param {number} userId? - The id of the logged in admin
   * @param {Agent}  user? - A copy of the entire agent object
   * @param {string} userEmail? - The email of the admin or agent logged in
   * @param {string} listViewSelection? - An object reference to the view selected on the CRM (table, kanban)
   * @param {any}    XILO_DASHBOARD_FILTERS? - An array of objects for filtering the CRM
   * @param {any}    features? - A json object containing this users permissions
   * @param {boolean}isContractSigned? - Flag on whether contract was signed or not
   * @param {string} slackUrl? - Authenticated slack url
   * @param {string} microsoftUrl? - Url used to authenticate microsoft accounts
   * @param {string} googleUrl? - Url used to authenticate google accounts
   * @param {string} userType? - User type that is not associated to our standard roles but to onboarding
   * @param {any}    copiedSection? - Object containing form Section data that will be pasted into the form builder
   * @param {any}    copiedQuestionGroup? - Object containing form Question Group data that will be pasted into the form builder
   * @param {any}    copiedQuestion? - Object containing form Question data that will be pasted into the form builder
   * @param {any}    copiedObject? - Old object containing form data that will be pasted into the form builder
   * @param {any}    formAnalytics? - Unique id for form analytics sessions for
   * @param {boolean}showEdit? - Flag on the old clients table that shows edit client or not
   * @returns {boolean} - true if no errors
   */
  setLocalStorageParams (params: LocalStorageParams) {
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'object') {
        localStorage.setItem(key, JSON.stringify(value))
      } else if (typeof value === 'number') {
        localStorage.setItem(key, value.toString())
      } else {
        localStorage.setItem(key, value);
      }
    });
    return true;
  }
}
